export function ActiveClassNamePlugin(className = "is-active") {
	return (slider) => {
		const setActiveSlides = ({ options, track, slides }) => {
			const {
				slides: { perView },
			} = options;
			const {
				details: { rel },
			} = track;

			slides.forEach((slide, index) => {
				slide.classList.toggle(
					className,
					index >= rel && index < rel + perView
				);
			});
		};

		slider.on("created", setActiveSlides);
		slider.on("slideChanged", setActiveSlides);
		slider.on("optionsChanged", setActiveSlides);
	};
}

export function ArrowsPlugin({ container, className }) {

	// Get the PolyLang translation strings
	const translate = window.emYtkBlocksConfig;
	
	return (slider) => {
		const arrows = document.createElement("div");
		arrows.classList.add(className);

		const prev = document.createElement("button");
		prev.classList.add(`${className}__prev`);
		prev.setAttribute("aria-label", translate?.text?.sliderViewPrev ?? "Previous slide");

		const next = document.createElement("button");
		next.classList.add(`${className}__next`);
		next.setAttribute("aria-label", translate?.text?.sliderViewNext ?? "Next slide");

		const setEvents = (slider) => {
			prev.addEventListener("click", slider.prev);
			next.addEventListener("click", slider.next);
		};

		const maybeDisableArrows = ({ options, track, slides }) => {
			const {
				slides: { perView },
			} = options;
			const {
				details: { rel },
			} = track;

			prev.disabled = rel === 0;
			next.disabled = rel === slides.length - perView;
		};

		slider.on("created", (slider) => {
			arrows.appendChild(prev);
			arrows.appendChild(next);
			container.appendChild(arrows);

			setEvents(slider);
			maybeDisableArrows(slider);
		});

		slider.on("slideChanged", maybeDisableArrows);
		slider.on("optionsChanged", maybeDisableArrows);
	};
}

export function CreatedClassNamePlugin(className = "is-created") {
	return (slider) => {
		slider.on("created", ({ container }) => {
			container.classList.add(className);
		});
	};
}

export function DotsPlugin({ container, className }) {

	// Get the PolyLang translation strings
	const translate = window.emYtkBlocksConfig;

	return (slider) => {
		const dots = document.createElement("div");
		dots.classList.add(className);

		const buildDots = ({ options, slides, moveToIdx }) => {
			const {
				slides: { perView },
			} = options;

			const numOfDots = slides.length - perView + 1;

			dots.innerHTML = "";

			for (let i = 0; i < numOfDots; i++) {
				const button = document.createElement("button");
				button.classList.add("slider-dot");
				button.addEventListener("click", () => moveToIdx(i));
				dots.appendChild(button);
			}
		};

		const setActiveDots = ({ track }) => {
			const {
				details: { rel },
			} = track;
			const buttons = dots.querySelectorAll("button");

			buttons.forEach((button, index) => {
				button.classList.toggle("is-active", rel === index);

				rel === index
					? button.setAttribute("aria-label", translate?.text?.sliderViewCurrent ?? "Current slider view")
					: button.setAttribute("aria-label", `${translate?.text?.sliderViewMove ?? "Move to slider view"} ${index + 1}`);
			});
		};

		slider.on("created", (slider) => {
			container.appendChild(dots);
			buildDots(slider);
			setActiveDots(slider);
		});

		slider.on("slideChanged", setActiveDots);
		slider.on("optionsChanged", (slider) => {
			buildDots(slider);
			setActiveDots(slider);
		});
	};
}

export function ImmobileWhenAllSlidesVisible(
	dataAttributeName = "data-keen-slider-immobile"
) {
	return (slider) => {
		let originalRenderMode = slider.options.renderMode.toString();
		const style = document.createElement("style");
		document.head.append(style);

		const setMobility = ({ options, slides, container }) => {
			style.textContent = `
				.keen-slider[${dataAttributeName}] {
					display: flex;
					flex-flow: row nowrap;
					gap: ${options?.slides?.spacing || 0}px;
				}
				.keen-slider[${dataAttributeName}] .keen-slider__slide {
					transform: none !important;
				}
			`;

			if (options?.slides?.perView >= slides.length) {
				slider.options.renderMode = "custom";
				container.setAttribute(dataAttributeName, "");
			} else {
				slider.options.renderMode = originalRenderMode;
				container.removeAttribute(dataAttributeName);
			}
		};

		slider.on("created", setMobility);
		slider.on("optionsChanged", setMobility);
	};
}

export function ScreenReaderTextPlugin(options = {}) {

	// Get the PolyLang translation strings
	const translate = window.emYtkBlocksConfig;

	const defaults = {
		className: "screen-reader-text",
		currentSlideText: translate?.text?.sliderViewCurrent ?? "Current slider view",
	};

	const { className, currentSlideText } = { ...defaults, ...options };

	return (slider) => {
		const style = document.createElement("style");
		style.textContent = `
			.keen-slider.screen-reader-text {
				border: 0;
				clip: rect(1px, 1px, 1px, 1px);
				clip-path: inset(50%);
				height: 1px;
				margin: -1px;
				overflow: hidden;
				padding: 0;
				position: absolute;
				width: 1px;
				word-wrap: normal !important;
			}
		`;
		document.head.append(style);

		const createNodes = (slider) => {
			slider.slides.forEach((slide) => {
				const existingNode = slide.querySelector(`.${className}`);

				if (existingNode) {
					return false;
				}

				const screenReaderText = document.createElement("span");
				screenReaderText.classList.add(className);
				slide.appendChild(screenReaderText);
			});
		};

		const setCurrentSlide = (slider) => {
			slider.slides.forEach((slide, index) => {
				const screenReaderText = slide.querySelector(`.${className}`);

				screenReaderText.innerText =
					slider.track.details.rel === index ? currentSlideText : "";
			});
		};

		slider.on("created", (slider) => {
			createNodes(slider);
			setCurrentSlide(slider);
		});

		slider.on("optionsChanged", (slider) => {
			createNodes(slider);
			setCurrentSlide(slider);
		});

		slider.on("slideChanged", setCurrentSlide);
	};
}
